import * as helios from "@hyperionbt/helios";
import {
  CardanoNetwork,
  KreateUTxO,
} from "kreate-common/modules/business-types";

import ScriptAuction from "../../onchain/scriptAuctionEnglish.hl";
import {
  addHeliosCollateral,
  getCardanoNetworkParams,
  toHeliosDatum,
  toHeliosUTxO,
} from "../../utils";

// Must sync this with `kreate-protocol/onchain/scriptAuction.hl`
// We can pass it as a param here but it's meaningless if the
// compiled hash doesn't match the created Auction.
const MIN_LOVELACE = 3_000_000;

type Params = {
  artworkTitle: string;
  network: CardanoNetwork;
  wallet: helios.Cip30Wallet;
  treasuryAddress: helios.Address;
  auctionUTxO: KreateUTxO;
  bidderAddress: helios.Address;
  newBidAmount: bigint; // lovelace
};

export async function buildTx$BidAuction({
  artworkTitle,
  network,
  wallet,
  treasuryAddress,
  auctionUTxO,
  bidderAddress,
  newBidAmount,
}: Params) {
  const walletHelper = new helios.WalletHelper(wallet);
  const address = await walletHelper.baseAddress;
  const [utxos, remainingUTxOs] = await walletHelper.pickUtxos(
    new helios.Value(newBidAmount + BigInt(3_000_000)),
    helios.CoinSelection.selectLargestFirst
  );

  if (auctionUTxO.datum.type !== "auction-english") {
    throw new Error("wrong datum type for auction UTxO");
  }
  const auctionDatum = auctionUTxO.datum.value;
  const auctionHeliosUTxO = toHeliosUTxO(auctionUTxO);

  const tx = new helios.Tx();
  await addHeliosCollateral(wallet, tx);

  tx.addInputs(utxos);
  tx.addInput(auctionHeliosUTxO, new helios.ConstrData(0, []));
  const scriptAuction = new ScriptAuction({
    TREASURY_ADDRESS: treasuryAddress,
  }).compile(true);
  tx.attachScript(scriptAuction);

  tx.addOutput(
    new helios.TxOutput(
      auctionHeliosUTxO.origOutput.address,
      new helios.Value(
        newBidAmount + BigInt(MIN_LOVELACE),
        auctionHeliosUTxO.origOutput.value.assets
      ),
      toHeliosDatum({
        type: "auction-english",
        value: {
          ...auctionDatum,
          bidderAddress: bidderAddress.toBech32(),
        },
      })
    )
  );

  const returningAmount =
    auctionHeliosUTxO.value.lovelace - BigInt(MIN_LOVELACE);
  if (returningAmount > 0) {
    tx.addOutput(
      new helios.TxOutput(
        new helios.Address(auctionDatum.bidderAddress),
        new helios.Value(
          auctionHeliosUTxO.value.lovelace - BigInt(MIN_LOVELACE)
        )
      )
    );
  }

  /** NOTE: @thanh-kreate
   * Since helios api does not automatically break a string that is less than 64
   * characters to array, this should be done manually to satisfy CIP-20 constraints
   * More details: https://cips.cardano.org/cips/cip20/
   * */
  const txMessage = `A new bid of ${
    Number(newBidAmount) / 1_000_000
  } ₳ was placed on 『${artworkTitle}』!`;
  tx.addMetadata(674, {
    map: [
      [
        "msg",
        helios.textToBytes(txMessage).length <= 64 ? [txMessage] : txMessage,
      ],
    ],
  });

  tx.validTo(
    new Date(Math.min(Date.now() + 5 * 60 * 1000, auctionDatum.deadline - 1))
  );

  await tx.finalize(getCardanoNetworkParams(network), address, remainingUTxOs);

  const isSelfOutbidding =
    auctionDatum.bidderAddress === bidderAddress.toBech32();

  return {
    tx,
    bidFee: isSelfOutbidding
      ? newBidAmount - (auctionHeliosUTxO.value.lovelace - BigInt(MIN_LOVELACE))
      : newBidAmount,
    txFee: tx.body.fee,
    isSelfOutbidding,
  };
}

/** @deprecated __@kien-kreate__: use `buildTx$BidAuction` instead */
export default async function bidAuction(
  artworkTitle: string,
  network: CardanoNetwork,
  wallet: helios.Cip30Wallet,
  treasuryAddress: helios.Address,
  auctionUTxO: KreateUTxO,
  bidderAddress: helios.Address,
  newBidAmount: bigint // lovelace
): Promise<helios.TxId> {
  const { tx } = await buildTx$BidAuction({
    artworkTitle,
    network,
    wallet,
    treasuryAddress,
    auctionUTxO,
    bidderAddress,
    newBidAmount,
  });
  const signatures = await wallet.signTx(tx);
  tx.addSignatures(signatures);

  return wallet.submitTx(tx);
}
